import { G } from '@mobily/ts-belt';
import Warning from '@mui/icons-material/Warning';
import { Box, Typography } from '@mui/material';
import { Decimal } from 'decimal.js';
import { useMemo } from 'react';
import { CheckboxGroupInput, required, SelectField, useGetOne, useTranslate } from 'react-admin';
import { useWatch } from 'react-hook-form';
import { useQuery } from 'react-query';

import { isFullyManagedPlan } from '@Common/types/b2b/plans';
import { GetB2bEmployeeDepositBankInfoEndpoint } from '@Common/types/bo/b2b/employees';
import {
  BoVoluntaryDepositPaymentMethod,
  PostB2bIndividualOperationSummaryEndpoint,
} from '@Common/types/bo/b2b/individual_operations';

import {
  b2bRecurringInvestmentFrequencyChoices,
  b2bRecurringInvestmentPayerChoices,
} from '@sh4res/bo-common/choices/recurring_investments';
import { voluntaryPaymentTypeChoices } from '@sh4res/bo-common/components/B2bVoluntaryPayment/choices';
import { PlanSelectChoice } from '@sh4res/bo-common/components/B2bVoluntaryPayment/components/PlanSelectInput';
import { RadioButtonGroupInput } from '@sh4res/bo-common/components/RadioButtonGroupInput';
import { ShowSection } from '@sh4res/bo-common/components/ShowSection';
import { TypographyTranslated } from '@sh4res/bo-common/components/TypographyTranslated';
import { useCreatePromise } from '@sh4res/bo-common/hooks';
import { formatCentsNumber } from '@sh4res/bo-common/utils';

type AmountRow = {
  saving_plan_id: number | null;
  instrument_id: number | null;
  amount: number | null;
  plan_data: PlanSelectChoice | null;
  deductibility: string | null;
};

type VoluntaryDepositSummaryRequest = Extract<
  PostB2bIndividualOperationSummaryEndpoint['Client']['RequestBody'],
  { type: 'voluntary_deposit' }
>;
type VoluntaryDepositSummaryResponse = Extract<
  PostB2bIndividualOperationSummaryEndpoint['Client']['ResponseBody'],
  { total_entry_fee_employee_amount: number }
>;
type SummaryRequestRow = VoluntaryDepositSummaryRequest['rows'][number];

/**
 * Groups the form rows by plan (and deductibility) into the shape expected by the summary
 * endpoint, mirroring the grouping done when the operation is actually created:
 * fully managed plans carry a single amount, libre plans carry their instrument allocations.
 */
const buildSummaryRows = (rows: AmountRow[]): SummaryRequestRow[] => {
  const rowsByPlan = new Map<string, SummaryRequestRow>();

  for (const row of rows) {
    if (row.saving_plan_id === null || row.amount === null) {
      continue;
    }

    const isManaged = row.plan_data ? isFullyManagedPlan(row.plan_data.type) : false;
    const key = `${row.saving_plan_id}-${row.deductibility || 'none'}`;

    if (!rowsByPlan.has(key)) {
      rowsByPlan.set(
        key,
        isManaged
          ? { saving_plan_id: row.saving_plan_id, amount: row.amount }
          : { saving_plan_id: row.saving_plan_id, allocations: [] },
      );
    }

    const entry = rowsByPlan.get(key);
    if (entry && !isManaged && row.instrument_id !== null) {
      entry.allocations?.push({ instrument_id: row.instrument_id, amount: row.amount });
    }
  }

  return Array.from(rowsByPlan.values()).filter(
    (entry) => G.isNotNullable(entry.amount) || (entry.allocations?.length ?? 0) > 0,
  );
};

const testId = 'BoExternalB2bVoluntaryPaymentCreateConfirmationStep';

export const ConfirmationStep = ({ recurring = false }: { recurring?: boolean }) => {
  const translate = useTranslate();

  const [payer, startDate, frequency]: [
    string | undefined,
    string | undefined,
    string | undefined,
  ] = useWatch({ name: ['payer', 'start_date', 'frequency'] });

  const [employeeId, rows, type, payment_method]: [
    number | null,
    AmountRow[],
    'voluntary_payment',
    BoVoluntaryDepositPaymentMethod,
  ] = useWatch({
    name: ['b2b_employee_id', 'rows', 'type', 'payment_method'],
  });
  const amountRows = useMemo(() => rows, [rows]);

  const totalAmount = amountRows.reduce((total, row) => total.add(row.amount ?? 0), new Decimal(0));

  const summaryRows = useMemo(() => buildSummaryRows(amountRows), [amountRows]);

  const [getIndividualOperationSummary] = useCreatePromise<
    VoluntaryDepositSummaryRequest,
    VoluntaryDepositSummaryResponse
  >();

  const { data: summary } = useQuery(
    ['individualOperationSummary', employeeId, summaryRows],
    async () =>
      getIndividualOperationSummary('b2b/individual_operations/summary', {
        data: {
          type: 'voluntary_deposit',
          b2b_employee_id: employeeId as number,
          rows: summaryRows,
        },
      }),
    { enabled: employeeId !== null && summaryRows.length > 0 },
  );

  const { data: depositBankInfo } = useGetOne<
    GetB2bEmployeeDepositBankInfoEndpoint['Client']['ResponseBody']
  >('b2b/employees/deposit_bank_info', { id: employeeId ?? 0 }, { enabled: employeeId !== null });
  const depositBankAccount = depositBankInfo?.deposit_bank_account;

  return (
    <>
      <ShowSection
        leftAligned
        label="resources.bo_common.b2b.voluntary_payment_create.sections.summary_confirmation"
        noCard={true}>
        {recurring ? (
          <Box>
            <Typography>
              {translate('resources.bo_common.b2b.recurring_investments.create.summary.type')}:{' '}
              {translate('resources.bo_common.b2b.recurring_investments.create.type')}
            </Typography>
            <Typography>
              {translate(
                'resources.bo_common.b2b.recurring_investments.create.summary.total_per_occurrence',
              )}
              : {formatCentsNumber(totalAmount.toNumber())}
            </Typography>
            <Typography>
              {translate(
                'resources.bo_common.b2b.recurring_investments.create.summary.total_mc_per_occurrence',
              )}
              : {formatCentsNumber(summary?.total_matching_contribution_gross_amount ?? 0)}
            </Typography>
            <Typography>
              {translate('resources.bo_common.b2b.recurring_investments.fields.payer')}:{' '}
              <SelectField
                data-testid={`${testId}_PayerField`}
                record={{ payer }}
                source="payer"
                choices={b2bRecurringInvestmentPayerChoices}
              />
            </Typography>
            <Typography>
              {translate(
                'resources.bo_common.b2b.recurring_investments.fields.execution_starting_date',
              )}
              : {startDate ? startDate.split('-').reverse().join('/') : ''}
            </Typography>
            <Typography>
              {translate(
                'resources.bo_common.b2b.recurring_investments.fields.execution_frequency',
              )}
              :{' '}
              <SelectField
                data-testid={`${testId}_FrequencyField`}
                record={{ frequency }}
                source="frequency"
                choices={b2bRecurringInvestmentFrequencyChoices}
              />
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              {translate('resources.bo_common.b2b.recurring_investments.create.summary.mc_ceiling')}
            </Typography>
          </Box>
        ) : (
          <Box>
            <Typography>
              {translate('resources.bo_common.b2b.voluntary_payment_create.sections.type')}:{' '}
              <SelectField
                data-testid={`${testId}_TypeField`}
                record={{ type }}
                source="type"
                choices={voluntaryPaymentTypeChoices}
              />
            </Typography>
            <Typography>
              {translate('resources.bo_common.b2b.voluntary_payment_create.fields.total_invested')}:{' '}
              {formatCentsNumber(totalAmount.toNumber())}
            </Typography>
            <Typography>
              {translate(
                'resources.bo_common.b2b.voluntary_payment_create.fields.total_entry_fee_employee',
              )}
              : {formatCentsNumber(summary?.total_entry_fee_employee_amount ?? 0)}
            </Typography>
            <Typography>
              {translate(
                'resources.bo_common.b2b.voluntary_payment_create.fields.total_entry_fee_company',
              )}
              : {formatCentsNumber(summary?.total_entry_fee_company_amount ?? 0)}
            </Typography>
            <Typography>
              {translate(
                'resources.bo_common.b2b.voluntary_payment_create.fields.total_matching_contribution',
              )}
              : {formatCentsNumber(summary?.total_matching_contribution_gross_amount ?? 0)}
            </Typography>
          </Box>
        )}
      </ShowSection>
      <ShowSection
        leftAligned
        label="resources.bo_common.b2b.voluntary_payment_create.sections.payment"
        noCard={true}>
        <RadioButtonGroupInput
          data-testid={`${testId}_PaymentMethodInput`}
          source="payment_method"
          disabled={true}
          choices={[
            ...(recurring
              ? []
              : [
                  {
                    id: 'regular-bank',
                    name: 'resources.bo_common.b2b.voluntary_payment_create.choices.payment_method.regular_bank',
                  },
                ]),
            {
              id: 'sepa-direct-debit',
              name: 'resources.bo_common.b2b.voluntary_payment_create.choices.payment_method.sepa_direct_debit',
            },
          ]}
        />
        {payment_method === 'regular-bank' && depositBankAccount && (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', flexDirection: 'row' }}>
              <Warning style={{ color: '#FF6213', marginTop: '2px' }} />
              <TypographyTranslated label="resources.bo_common.b2b.voluntary_payment_create.messages.bank_info_line_1" />
            </Box>
            <TypographyTranslated
              label="resources.bo_common.b2b.voluntary_payment_create.messages.bank_info_beneficiary"
              variables={{ name: depositBankAccount.account_holder_name }}
            />
            <TypographyTranslated
              label="resources.bo_common.b2b.voluntary_payment_create.messages.bank_info_iban"
              variables={{ iban: depositBankAccount.iban }}
            />
            <TypographyTranslated
              label="resources.bo_common.b2b.voluntary_payment_create.messages.bank_info_bic"
              variables={{ bic: depositBankAccount.bic }}
            />
            <TypographyTranslated
              label="resources.bo_common.b2b.voluntary_payment_create.messages.bank_info_reference"
              variables={{ reference: depositBankAccount.reference }}
            />
          </Box>
        )}
      </ShowSection>
      <ShowSection
        leftAligned
        label="resources.bo_common.b2b.voluntary_payment_create.sections.confirmations"
        noCard={true}>
        <CheckboxGroupInput
          data-testid={`${testId}_ConfirmAccuracyInput`}
          source="confirm_accuracy"
          sx={{ marginLeft: '5px' }}
          label={false}
          isRequired
          validate={[required()]}
          choices={[
            {
              id: 'yes',
              name: 'resources.bo_common.b2b.voluntary_payment_create.sections.confirm_accuracy',
            },
          ]}
        />
      </ShowSection>
    </>
  );
};
